import { useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { deleteAccountMediaByUrl, uploadAccountMedia } from 'api/media';
import axios from 'axios';
import * as SettingsCard from 'components/settings-card';
import * as Tooltip from 'components/tooltip';
import { Upload, UploadRef } from 'components/upload';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

const CoverPhotoSettings = () => {
    const { accountId, cover } = useCurrentAccount();
    const toast = useToast();
    const ref = useRef<UploadRef>(null);

    const { isPending: isDeleting, mutate: deletePhoto } = useMutation({
        mutationFn: (url: string) => deleteAccountMediaByUrl(accountId, url),
        onSuccess: () => {
            toast.success('Deleted cover photo!');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.current(accountId) });
        },
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? 'Failed to delete cover photo'),
    });

    return (
        <SettingsCard.Root>
            <SettingsCard.LeftSide>
                <SettingsCard.Heading className="flex gap-2">
                    Cover Photo
                    <Tooltip.Root>
                        <Tooltip.Trigger>
                            <FontAwesomeIcon icon={faQuestionCircle} size="sm" />
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content inverse>
                                <Tooltip.Arrow inverse />
                                <p>Recommended dimensions: 1200 x 630px. Max Size: 1MB.</p>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </SettingsCard.Heading>
            </SettingsCard.LeftSide>
            <SettingsCard.RightSide className="flex flex-col gap-4">
                <Upload
                    ref={ref}
                    variant="single"
                    imagePreviewHeight={250}
                    defaultFiles={cover?.url ? [{ id: 'cover', url: cover.url }] : []}
                    acceptedFileTypes={['image/*']}
                    allowReorder={false}
                    allowMultiple={false}
                    // @ts-ignore
                    allowRemove={false}
                    strategy="remote"
                    uploadFn={async file => {
                        const { data } = await uploadAccountMedia(accountId, file, 'cover');

                        return data.url;
                    }}
                    onUpload={() => {
                        toast.success('Uploaded cover photo successfully!');
                        queryClient.invalidateQueries({
                            queryKey: accountQueryKeys.current(accountId),
                        });
                    }}
                    onUploadError={e => {
                        if (axios.isAxiosError(e)) {
                            toast.error(e.response?.data.message ?? 'Failed to upload photo');
                        }
                    }}
                    onremovefile={(_, file) => deletePhoto(file.getMetadata('url'))}
                />
                {cover && (
                    <div>
                        <Button
                            variant="solid"
                            disabled={isDeleting}
                            onClick={() => ref.current?.removeFiles()}
                            color="error"
                            size="sm"
                        >
                            Remove cover photo
                        </Button>
                    </div>
                )}
            </SettingsCard.RightSide>
        </SettingsCard.Root>
    );
};

export { CoverPhotoSettings };
